import { inject, Injectable, Injector } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { BaseNestedFieldDefinition, FieldType } from '../../../core/field-definitions/entities/field-definition.entity';
import { ProfileDataService } from '../../../core/profiles/data-services/profile.data-service';
import {
    ExtendedGenericListProfileField,
    ExtendedProfileField
} from '../../../core/profiles/entities/extended-profile-field.entity';

import { SharedLanguageService } from '@language';
import { User } from '../../../core/users/entities/user.entity';
import { UserService } from '../../../core/users/services/user.service';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { SortInfo } from '../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { SortOption } from '../../generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { ColumnType } from '../entities/column-type.entity';
import { RecordColumn } from '../entities/record-column.entity';
import { SortOptionsHelper } from '../helpers/sort-options.helper';
import { ListViewStateService } from '../state-machines/list-view-state.service';
import { UnsupportedColumnService } from './base-columns/undefined-column.service';
import { BooleanFieldColumnService } from './field-columns/boolean-field-column.service';
import { DateFieldColumnService } from './field-columns/date-field-column.service';
import { TextFieldColumnService } from './field-columns/text-field-column.service';
import { UrlFieldColumnService } from './field-columns/url-field-column.service';
import { EstimatedPermanentDeleteDateColumnService } from './field-override-columns/estimated-permanent-delete-date-column.service';
import { FileSizeColumnService } from './field-override-columns/file-size-column.service';
import { ParentColumnService } from './field-override-columns/parent-column.service';
import { RecordStatusColumnService } from './field-override-columns/record-status-column.service';
import { RecordTypeColumnService } from './field-override-columns/record-type-column.service';
import { ThumbnailColumnService } from './field-override-columns/thumbnail-column.service';
import { VersioningStatusColumnService } from './field-override-columns/versioning-status-column.service';

@Injectable({ providedIn: 'root' })
export class ListProfileColumnService {
    private readonly COLUMN_SERVICE_MAPPING: Record<ColumnType, any> = {
        // Defaults
        Text: TextFieldColumnService,
        Boolean: BooleanFieldColumnService,
        Date: DateFieldColumnService,
        Url: UrlFieldColumnService,
        Unsupported: UnsupportedColumnService,
        // Overrides
        Thumbnail: ThumbnailColumnService,
        RecordStatus: RecordStatusColumnService,
        RecordType: RecordTypeColumnService,
        Parent: ParentColumnService,
        EstimatedPermanentDeleteDate: EstimatedPermanentDeleteDateColumnService,
        VersioningStatus: VersioningStatusColumnService,
        FileSize: FileSizeColumnService
    };

    private readonly refreshList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private readonly profileService: ProfileDataService = inject(ProfileDataService);
    private readonly listViewStateService: ListViewStateService = inject(ListViewStateService);

    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);
    private readonly sortOptionsHelper: SortOptionsHelper = new SortOptionsHelper(this.translateService);

    private readonly injector: Injector = inject(Injector);
    private readonly userService: UserService = inject(UserService);

    // #region Get Visible columns
    private getProfileFields(profileTag: string): Observable<ExtendedGenericListProfileField[]> {
        return this.userService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(orgId => this.profileService.getListProfileFields(orgId, profileTag)),
            shareReplay(1)
        );
    }

    public getAllProfileColumns(profileTag: string): Observable<RecordColumn[]> {
        return this.getProfileFields(profileTag).pipe(
            map(profileFields => profileFields.map(this.mapFieldToColumn.bind(this))),
            shareReplay(1)
        );
    }

    public getVisibleProfileColumns(profileTag: string, searchPageKey: string): Observable<RecordColumn[]> {
        const profileFields$ = this.getProfileFields(profileTag);

        const visibleFields$ = combineLatest([this.refreshList$, profileFields$]).pipe(
            map(([refresh, fields]) => this.filterVisibleFields(fields, searchPageKey))
        );

        const recordColumns$ = visibleFields$.pipe(
            map(profileFields => profileFields.map(this.mapFieldToColumn.bind(this)))
        );

        return recordColumns$;
    }

    private filterVisibleFields(
        profileFields: ExtendedGenericListProfileField[],
        searchPageKey: string
    ): ExtendedGenericListProfileField[] {
        const listViewState = this.listViewStateService.getListViewState(searchPageKey);

        if (!listViewState) {
            return profileFields.filter(field => field.DefaultVisible);
        }

        return profileFields.filter(field => listViewState.includes(field.DottedKey));
    }

    private mapFieldToColumn(profileField: ExtendedProfileField): RecordColumn {
        const columnType: ColumnType = ListProfileColumnService.getColumnType(profileField.FieldDefinition);

        const service = this.injector.get<any>(this.COLUMN_SERVICE_MAPPING[columnType]);
        return service.getColumn(profileField);
    }

    private static getOverrideColumnType(fieldDefinition: BaseNestedFieldDefinition): ColumnType | undefined {
        const dottedKey: string = fieldDefinition.DottedKey;

        switch (dottedKey) {
            case 'Internal.PathToKeyframeThumb':
                return 'Thumbnail';
            case 'Administrative.RecordStatus':
                return 'RecordStatus';
            case 'Administrative.RecordType':
                return 'RecordType';
            case 'Structural.ParentRecordId':
                return 'Parent';
            case 'Administrative.LogicalDeletionDate':
                return 'EstimatedPermanentDeleteDate';
            case 'Technical.FileSize':
                return 'FileSize';
            case 'Structural.Versioning.Status':
                return 'VersioningStatus';
            default:
                return undefined;
        }
    }

    public static getColumnType(fieldDefinition: BaseNestedFieldDefinition): ColumnType {
        const overrideColumn: ColumnType | undefined = this.getOverrideColumnType(fieldDefinition);

        if (overrideColumn) {
            return overrideColumn;
        }

        const type: FieldType = fieldDefinition.Type;

        switch (type) {
            case FieldType.SimpleField:
            case FieldType.DoubleField:
            case FieldType.EDTFField:
            case FieldType.EnumField:
            case FieldType.FramesField:
            case FieldType.GeoCoordinateField:
            case FieldType.LongField:
            case FieldType.ThesaurusLabelField:
                return 'Text';
            case FieldType.DateField:
                return 'Date';
            case FieldType.BooleanField:
                return 'Boolean';
            case FieldType.UrlField:
                return 'Url';
            default:
                return 'Unsupported';
        }
    }
    // #endregion

    // #region Set Visible columns
    public setVisibleProfileColumns(searchPageKey: string, columnKeys: string[]): void {
        this.listViewStateService.setListViewState(searchPageKey, columnKeys);
        this.refreshList$.next(true);
    }
    // #endregion

    // #region Column options
    public getColumnOptions(profileTag: string): Observable<FormSelectOption[]> {
        const profileFields$ = this.userService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(orgId => this.profileService.getListProfileFields(orgId, profileTag))
        );

        return profileFields$.pipe(
            map(fields =>
                fields.map(field => ({
                    label: field.DisplayLabel,
                    value: field.DottedKey
                }))
            )
        );
    }

    public getSortOptions(profileTag: string, defaultSortInfo: SortInfo[]): Observable<SortOption[]> {
        const profileFields$ = this.userService.getCurrentUser().pipe(
            map((user: User) => user.Organisation.Id),
            switchMap(orgId => this.profileService.getListProfileFields(orgId, profileTag))
        );

        const columns$ = profileFields$.pipe(map(profileFields => profileFields.map(this.mapFieldToColumn.bind(this))));

        return columns$.pipe(map(columns => this.sortOptionsHelper.getColumnSortOptions(columns, defaultSortInfo)));
    }
    // #endregion
}
